import React, { useState } from 'react';
import { projects } from '../mock';
import { Terminal, Lock, CheckCircle, Clock } from 'lucide-react';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active':
        return <Clock size={16} style={{ color: '#00FFD1' }} />;
      case 'Completed':
        return <CheckCircle size={16} style={{ color: '#00FFD1' }} />;
      default:
        return <Terminal size={16} style={{ color: '#00FFD1' }} />;
    }
  };

  const getClassificationColor = (classification) => {
    switch (classification) {
      case 'CLASSIFIED':
        return '#FF0055';
      case 'RESTRICTED':
        return '#FF9500';
      case 'PRODUCTION':
        return '#00FFD1';
      default:
        return '#00FFD1';
    }
  };

  return (
    <section id="projects" style={{
      minHeight: '100vh',
      padding: '160px 7.6923% 100px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Section header */}
        <div style={{ marginBottom: '80px', textAlign: 'center' }}>
          <div style={{
            fontFamily: 'monospace',
            fontSize: '14px',
            color: '#00FFD1',
            marginBottom: '16px',
            letterSpacing: '2px'
          }}>
            {'>> OPERATION ARCHIVES'}
          </div>
          <h2 style={{
            fontSize: '48px',
            fontWeight: 600,
            lineHeight: 1.1,
            color: '#FFFFFF',
            marginBottom: '24px'
          }}>
            Mission Logs
          </h2>
        </div>

        {/* Projects grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
          gap: '32px'
        }}>
          {projects.map((project) => (
            <div
              key={project.id}
              onClick={() => setSelectedProject(project)}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(0, 255, 209, 0.2)',
                padding: '32px',
                cursor: 'pointer',
                transition: 'all 0.4s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.border = '1px solid rgba(0, 255, 209, 0.6)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.transform = 'translateY(-8px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.border = '1px solid rgba(0, 255, 209, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Classification badge */}
              <div style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                background: 'rgba(0, 0, 0, 0.6)',
                border: `1px solid ${getClassificationColor(project.classification)}`,
                fontSize: '11px',
                fontFamily: 'monospace',
                color: getClassificationColor(project.classification),
                letterSpacing: '1px'
              }}>
                <Lock size={12} />
                {project.classification}
              </div>

              {/* Project header */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '16px',
                marginTop: '24px'
              }}>
                {getStatusIcon(project.status)}
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    marginBottom: '8px',
                    fontFamily: 'monospace'
                  }}>
                    {project.name}
                  </h3>
                  <div style={{
                    fontSize: '14px',
                    color: '#00FFD1',
                    fontFamily: 'monospace'
                  }}>
                    {project.role}
                  </div>
                </div>
              </div>

              {/* Status */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                background: 'rgba(0, 255, 209, 0.1)',
                border: '1px solid rgba(0, 255, 209, 0.3)',
                fontSize: '12px',
                fontFamily: 'monospace',
                color: '#00FFD1',
                marginBottom: '16px'
              }}>
                STATUS: {project.status.toUpperCase()}
              </div>

              {/* Description preview */}
              <p style={{
                fontSize: '14px',
                lineHeight: 1.6,
                color: 'rgba(255, 255, 255, 0.7)',
                marginBottom: '20px',
                fontFamily: 'monospace'
              }}>
                {project.description.substring(0, 120)}...
              </p>

              {/* Tech tags */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '16px'
              }}>
                {project.technologies.slice(0, 3).map((tech, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '4px 12px',
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontFamily: 'monospace'
                    }}
                  >
                    {tech}
                  </span>
                ))}
                {project.technologies.length > 3 && (
                  <span style={{
                    padding: '4px 12px',
                    fontSize: '11px',
                    color: '#00FFD1',
                    fontFamily: 'monospace'
                  }}>
                    +{project.technologies.length - 3}
                  </span>
                )}
              </div>

              {/* Year */}
              <div style={{
                fontSize: '12px',
                color: '#4D4D4D',
                fontFamily: 'monospace',
                marginTop: '16px'
              }}>
                {project.year}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Project detail modal */}
      {selectedProject && (
        <div
          onClick={() => setSelectedProject(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.95)',
            backdropFilter: 'blur(10px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#000000',
              border: '2px solid #00FFD1',
              boxShadow: '0 0 40px rgba(0, 255, 209, 0.3)',
              padding: '48px',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative'
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedProject(null)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                color: '#00FFD1',
                fontSize: '24px',
                cursor: 'pointer',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 255, 209, 0.1)';
                e.currentTarget.style.transform = 'rotate(90deg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.transform = 'rotate(0deg)';
              }}
            >
              ×
            </button>

            {/* Modal content */}
            <div style={{
              fontFamily: 'monospace',
              fontSize: '12px',
              color: '#00FFD1',
              marginBottom: '24px',
              letterSpacing: '1px'
            }}>
              {'>> MISSION DETAILS'}
            </div>

            <h3 style={{
              fontSize: '32px',
              fontWeight: 600,
              color: '#FFFFFF',
              marginBottom: '16px'
            }}>
              {selectedProject.name}
            </h3>

            <div style={{
              fontSize: '18px',
              color: '#00FFD1',
              marginBottom: '24px'
            }}>
              {selectedProject.role}
            </div>

            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '32px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                padding: '8px 16px',
                background: 'rgba(0, 255, 209, 0.1)',
                border: '1px solid rgba(0, 255, 209, 0.3)',
                fontSize: '12px',
                color: '#00FFD1'
              }}>
                STATUS: {selectedProject.status.toUpperCase()}
              </div>
              <div style={{
                padding: '8px 16px',
                background: 'rgba(0, 0, 0, 0.6)',
                border: `1px solid ${getClassificationColor(selectedProject.classification)}`,
                fontSize: '12px',
                color: getClassificationColor(selectedProject.classification)
              }}>
                {selectedProject.classification}
              </div>
            </div>

            <p style={{
              fontSize: '16px',
              lineHeight: 1.8,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '32px'
            }}>
              {selectedProject.description}
            </p>

            <div style={{ marginBottom: '24px' }}>
              <div style={{
                fontSize: '14px',
                color: '#4D4D4D',
                marginBottom: '12px',
                letterSpacing: '1px'
              }}>
                TECHNOLOGIES:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {selectedProject.technologies.map((tech, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      fontSize: '14px',
                      color: '#FFFFFF'
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div style={{
              fontSize: '14px',
              color: '#4D4D4D'
            }}>
              TIMELINE: {selectedProject.year}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @media (max-width: 768px) {
          #projects > div > div:nth-child(2) {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Projects;